// Ravelocator logo — dramatic ear/speaker + soundwave burst icon
export default function Logo({ size = 40, showText = true, fontSize, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <LogoIcon size={size} />
      {showText && (
        <div className="flex flex-col leading-none">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: fontSize ?? size * 0.44,
              fontWeight: 800,
              letterSpacing: "0.12em",
              background: "linear-gradient(90deg, #00f5ff 0%, #4488ff 50%, #ff3399 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              textShadow: "none",
            }}
          >
            RAVELOCATOR
          </span>
        </div>
      )}
    </div>
  );
}

export function LogoIcon({ size = 40 }) {
  const id = `logo-${size}`; // unique ID per size to avoid SVG filter conflicts
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id={`${id}-bg`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ff3399" stopOpacity="0.18" />
          <stop offset="60%" stopColor="#4488ff" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-grad`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00f5ff" />
          <stop offset="40%" stopColor="#4488ff" />
          <stop offset="100%" stopColor="#ff3399" />
        </linearGradient>
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={`${id}-strong-glow`} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Dark background */}
      <circle cx="32" cy="32" r="31" fill="#050508" />
      <circle cx="32" cy="32" r="31" fill={`url(#${id}-bg)`} />

      {/* Outer ring — spinning dashes */}
      <circle
        cx="32" cy="32" r="29"
        stroke={`url(#${id}-grad)`}
        strokeWidth="1.5"
        strokeOpacity="0.6"
        strokeDasharray="6 3"
        filter={`url(#${id}-glow)`}
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 32 32"
          to="360 32 32"
          dur="12s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Sound waves — concentric arcs emanating from center-left */}
      {[8, 13, 18, 23].map((r, i) => (
        <path
          key={r}
          d={`M ${32 - r * 0.6} ${32 - r} A ${r} ${r} 0 0 1 ${32 - r * 0.6} ${32 + r}`}
          stroke={`url(#${id}-grad)`}
          strokeWidth={i === 3 ? "2" : "1.5"}
          strokeOpacity={1 - i * 0.18}
          strokeLinecap="round"
          fill="none"
          filter={`url(#${id}-glow)`}
        >
          <animate
            attributeName="stroke-opacity"
            values={`${1 - i * 0.18};${1 - i * 0.1};${1 - i * 0.18}`}
            dur={`${1.2 + i * 0.3}s`}
            repeatCount="indefinite"
          />
        </path>
      ))}

      {/* Vertical waveform bars — right side */}
      <g filter={`url(#${id}-glow)`}>
        {[
          { x: 37, h: 8,  dur: "0.5s" },
          { x: 41, h: 16, dur: "0.65s" },
          { x: 45, h: 26, dur: "0.45s" },
          { x: 49, h: 18, dur: "0.7s" },
          { x: 53, h: 10, dur: "0.55s" },
        ].map(({ x, h, dur }, i) => (
          <rect
            key={i}
            x={x}
            y={32 - h / 2}
            width={2.5}
            height={h}
            rx={1.25}
            fill={`url(#${id}-grad)`}
            fillOpacity="0.95"
          >
            <animate
              attributeName="height"
              values={`${h};${Math.min(h * 1.6, 40)};${h}`}
              dur={dur}
              repeatCount="indefinite"
            />
            <animate
              attributeName="y"
              values={`${32 - h / 2};${32 - Math.min(h * 1.6, 40) / 2};${32 - h / 2}`}
              dur={dur}
              repeatCount="indefinite"
            />
          </rect>
        ))}
      </g>

      {/* Center pulse dot */}
      <circle cx="32" cy="32" r="4" fill={`url(#${id}-grad)`} filter={`url(#${id}-strong-glow)`}>
        <animate attributeName="r" values="3;5;3" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.8;1;0.8" dur="1.8s" repeatCount="indefinite" />
      </circle>

      {/* Tiny detection dots on outer ring */}
      {[0, 120, 240].map((deg, i) => {
        const rad = (deg - 90) * Math.PI / 180;
        const x = 32 + 29 * Math.cos(rad);
        const y = 32 + 29 * Math.sin(rad);
        return (
          <circle key={i} cx={x} cy={y} r="2.5" fill={i === 0 ? "#ff3399" : i === 1 ? "#4488ff" : "#00ff88"}
            filter={`url(#${id}-strong-glow)`}>
            <animate attributeName="opacity" values="1;0.3;1" dur={`${1 + i * 0.4}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
    </svg>
  );
}
